import React, { useState } from 'react';
import makeStyles from '@material-ui/core/styles/makeStyles';
import Typography from '@material-ui/core/Typography';
import Link from '@material-ui/core/Link';
import Button from '@material-ui/core/Button';
import Divider from '@material-ui/core/Divider';
import Radio from '@material-ui/core/Radio';
import RadioGroup from '@material-ui/core/RadioGroup';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import WarningRoundedIcon from '@material-ui/icons/WarningRounded';
import GetAppRoundedIcon from '@material-ui/icons/GetAppRounded';
import { Formik, Form } from 'formik';
import { useParams } from 'react-router-dom';
import { useHistory } from 'react-router';
import { useData, useStatus } from 'resift';
import { useDispatch } from 'react-redux';
import importAndValidateFile from 'fetches/entitySourceFiles/importAndValidateFile';

import Page from 'components/Page/newComponents';
import PageFormToolbar from 'components/Page/newComponents/PageFormToolbar';
import SaveButton from 'components/Page/SaveButton';
import CancelButton from 'components/Page/CancelButton';
import FileDrop from 'components/FileDrop';
import checkCsvUploadFileType from 'helpers/dataSync/checkCsvUploadFileType';
import makePath from 'helpers/makePath';
import { Theme } from 'styles/ThemeTyping';
import Breadcrumbs from 'components/Page/Breadcrumbs';
import PageTitle from 'components/Page/PageTitle';
import PageCsvAnalysisDialog from 'pages/PageTypes/components/ImportPageDataCsvPage/PageCsvAnalysisDialog';

function ImportPageDataCsvPage() {
  const classes = useStyles();
  const history = useHistory();
  const [file, setFile] = useState<any>(null);
  const [errorText, setErrorText] = useState<null | string>(null);
  const [csvIssuesDialogOpen, setCsvIssuesDialogOpen] = useState(false);
  const { entityTypeKey } = useParams<{ entityTypeKey: string }>();
  const dispatch = useDispatch();
  const fileValidationResultsResponse = useData(importAndValidateFile);
  const fileValidationStatus = useStatus(importAndValidateFile);

  const handleFileChange = (newFile: any) => {
    if (!newFile) {
      setFile(null);
      setErrorText(null);
      return;
    }

    setFile(newFile);
    const fileTypeError = checkCsvUploadFileType(newFile);
    setErrorText(fileTypeError);
  };

  const handleStartImport = (deleteEntities: boolean) => {
    if (!file || !!errorText) {
      return;
    }

    dispatch(importAndValidateFile(entityTypeKey, file, deleteEntities));

    setCsvIssuesDialogOpen(true);
  };

  const handleNewUpload = () => {
    setCsvIssuesDialogOpen(false);
    setFile(null);
    setErrorText(null);
  };

  const handleBackToPagesTab = () => {
    setCsvIssuesDialogOpen(false);
    history.push(makePath('PageTypes', { entityTypeKey, tab: 'pages' }));
  };

  const handleDownloadTemplate = () => {
    const link = document.createElement('a');
    link.href = 'https://download.justsift.com/template/pages_upload_template.csv';
    link.download = '';
    link.click();
  };

  return (
    <Page classes={{ root: classes.pageRoot, content: classes.pageContent }}>
      <Breadcrumbs
        routes={['Home', { name: 'PageTypes', title: 'Pages', params: { entityTypeKey } }]}
      />
      <PageTitle
        title="Import Pages via CSV"
        subtitle="Create or update Pages in bulk by uploading a CSV file. Follow the steps below to ensure data is uploaded successfully."
      />
      <div className={classes.contentRow}>
        <section className={classes.leftPane}>
          <Typography variant="h6" className={classes.stepTitle}>
            Step 1: Download our CSV Template
          </Typography>
          <Typography className={classes.stepDescription} variant="body2">
            Use our CSV template to correctly format your data file. Each imported page must have a
            unique name.{' '}
            <Link
              href="http://help.justsift.com/en/articles/14047372-formatting-a-pages-csv-for-import"
              target="_blank"
              rel="noopener noreferrer"
              color="secondary"
            >
              Learn more
            </Link>{' '}
            about formatting the file.
          </Typography>
          <Button
            variant="outlined"
            className={classes.downloadButton}
            startIcon={<GetAppRoundedIcon />}
            onClick={handleDownloadTemplate}
          >
            Download File Template
          </Button>
        </section>

        <Divider orientation="vertical" flexItem className={classes.divider} />

        <Formik initialValues={{ shouldRemoveMissingRecords: 'no' }} onSubmit={() => {}}>
          {({ values, setFieldValue, resetForm }) => {
            const handleCancel = () => {
              resetForm();
              setFile(null);
              setErrorText(null);
              handleBackToPagesTab();
            };

            return (
              <Form className={classes.formRoot}>
                <section className={classes.rightPane}>
                  <Typography variant="h6" className={classes.stepTitle}>
                    Step 2: Upload CSV
                  </Typography>
                  <Typography className={classes.stepDescription} variant="body2">
                    Drop the correctly formatted file here
                  </Typography>

                  <div className={classes.fileDropWrapper}>
                    <FileDrop
                      hasError={!!errorText}
                      errorText={errorText}
                      onSetFile={setFile}
                      onFileChange={handleFileChange}
                      file={file}
                    />
                  </div>

                  <div className={classes.removeSection}>
                    <Typography className={classes.removeTitle} variant="body2">
                      Delete Pages from Sift that are not included in this file?
                    </Typography>
                    <div className={classes.cautionRow}>
                      <WarningRoundedIcon className={classes.warningIcon} fontSize="small" />
                      <Typography variant="caption" className={classes.cautionText}>
                        <strong>Caution</strong>: This will permanently remove any existing pages of
                        this type that are not present in the file.
                      </Typography>
                    </div>

                    <RadioGroup
                      row
                      value={values.shouldRemoveMissingRecords}
                      onChange={event =>
                        setFieldValue('shouldRemoveMissingRecords', event.target.value)
                      }
                    >
                      <FormControlLabel value="yes" control={<Radio />} label="Yes" />
                      <FormControlLabel value="no" control={<Radio />} label="No" />
                    </RadioGroup>
                  </div>
                </section>

                <PageFormToolbar dirty>
                  <CancelButton onClick={handleCancel} />
                  <SaveButton
                    label="Start Import"
                    onClick={() => handleStartImport(values.shouldRemoveMissingRecords === 'yes')}
                    disabled={!file || !!errorText}
                  />
                </PageFormToolbar>
              </Form>
            );
          }}
        </Formik>
      </div>

      {!!file && (
        <PageCsvAnalysisDialog
          open={csvIssuesDialogOpen}
          status={fileValidationStatus}
          validationResultsResponse={fileValidationResultsResponse}
          onBackToPagesTab={handleBackToPagesTab}
          onNewUpload={handleNewUpload}
          entityTypeKey={entityTypeKey}
        />
      )}
    </Page>
  );
}

const useStyles = makeStyles((theme: Theme) => {
  return {
    root: {},
    pageRoot: {
      display: 'flex',
      flex: '1 1 auto',
      height: 'calc(100vh - 64px)',
    },
    pageContent: {
      alignItems: 'stretch',
    },
    formRoot: {
      display: 'flex',
      flexDirection: 'column',
      flex: '1 1 auto',
    },
    contentRow: {
      display: 'flex',
      alignItems: 'stretch',
      maxWidth: 1100,
      width: '100%',
      paddingBottom: theme.spacing(4),
      marginTop: theme.spacing(4),
    },
    leftPane: {
      width: 320,
      paddingRight: theme.spacing(4),
    },
    rightPane: {
      flex: 1,
      paddingLeft: theme.spacing(4),
    },
    divider: {
      backgroundColor: theme.palette.divider,
    },
    stepTitle: {
      color: theme.palette.primary.main,
      marginBottom: theme.spacing(1),
    },
    stepDescription: {
      color: theme.palette.text.secondary,
      marginBottom: theme.spacing(3),
      maxWidth: 560,
    },
    downloadButton: {
      borderColor: theme.palette.common.black,
    },
    fileDropWrapper: {
      marginBottom: theme.spacing(4),
      maxWidth: 760,
    },
    removeSection: {
      maxWidth: 760,
    },
    removeTitle: {
      marginBottom: theme.spacing(1.5),
    },
    cautionRow: {
      display: 'flex',
      alignItems: 'center',
      marginBottom: theme.spacing(1.5),
    },
    warningIcon: {
      color: theme.palette.warning.main,
      marginRight: theme.spacing(1),
    },
    cautionText: {
      color: theme.palette.text.secondary,
    },
  };
});

export default ImportPageDataCsvPage;
